/**
 * Reusable Button component for trAInedup.ai
 * Supports both button and anchor elements with consistent styling
 */

import React from 'react';
import { LucideIcon } from 'lucide-react';

// Base button props
interface BaseButtonProps {
  children?: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'accent' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  icon?: LucideIcon;
  iconPosition?: 'left' | 'right';
  loading?: boolean;
  fullWidth?: boolean;
  className?: string;
}

// Button element props
interface ButtonProps extends BaseButtonProps, React.ButtonHTMLAttributes<HTMLButtonElement> {
  href?: never;
}

// Anchor element props  
interface LinkProps extends BaseButtonProps, React.AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string;
}

/**
 * Button component with support for different variants, sizes, and icons
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      icon: Icon,
      iconPosition = 'left',
      loading = false,
      fullWidth = false,
      className = '',
      ...props
    },
    ref
  ) => {
    // Variant styles
    const variantStyles = {
      primary: 'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500',
      secondary: 'bg-secondary-600 text-white hover:bg-secondary-700 focus:ring-secondary-500',
      accent: 'bg-accent-600 text-white hover:bg-accent-700 focus:ring-accent-500',
      outline: 'border-2 border-primary-600 text-primary-600 hover:bg-primary-50 focus:ring-primary-500',
      ghost: 'text-primary-600 hover:bg-primary-50 focus:ring-primary-500',
      danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
    };

    // Size styles
    const sizeStyles = {
      sm: 'px-3 py-2 text-sm',
      md: 'px-4 py-3 text-base',
      lg: 'px-6 py-4 text-lg',
    };

    // Base styles
    const baseStyles = `
      inline-flex items-center justify-center
      whitespace-nowrap
      font-medium rounded-lg
      transition-all duration-200
      focus:outline-none focus:ring-2 focus:ring-offset-2
      disabled:opacity-50 disabled:cursor-not-allowed
      ${fullWidth ? 'w-full' : ''}
      ${variantStyles[variant]}
      ${sizeStyles[size]}
      ${className}
    `.trim().replace(/\s+/g, ' ');

    // Icon spacing
    const hasVisualIndicator = Boolean(children) && (loading || Icon);
    const iconSpacing = hasVisualIndicator ? (size === 'sm' ? 'gap-2' : 'gap-3') : '';
    const combinedStyles = `${baseStyles} ${iconSpacing}`;

    const renderSpinner = () => (
      <svg
        className="animate-spin h-4 w-4"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    );

    const iconSizeStyles = {
      sm: 'h-4 w-4',
      md: 'h-5 w-5',
      lg: 'h-6 w-6',
    };

    const renderIcon = (position: 'left' | 'right') => {
      if (loading) {
        return position === 'left' ? renderSpinner() : null;
      }

      if (Icon && iconPosition === position) {
        return <Icon className={iconSizeStyles[size]} />;
      }

      return null;
    };

    // Render content
    const content = (
      <>
        {renderIcon('left')}
        {children}
        {renderIcon('right')}
      </>
    );

    return (
      <button
        ref={ref}
        className={combinedStyles}
        disabled={loading || props.disabled}
        {...props}
      >
        {content}
      </button>
    );
  }
);

Button.displayName = 'Button';

/**
 * Link component that looks like a button
 */
export const ButtonLink = React.forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      icon: Icon,
      iconPosition = 'left',
      loading = false,
      fullWidth = false,
      className = '',
      ...props
    },
    ref
  ) => {
    // Variant styles
    const variantStyles = {
      primary: 'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500',
      secondary: 'bg-secondary-600 text-white hover:bg-secondary-700 focus:ring-secondary-500',
      accent: 'bg-accent-600 text-white hover:bg-accent-700 focus:ring-accent-500',
      outline: 'border-2 border-primary-600 text-primary-600 hover:bg-primary-50 focus:ring-primary-500',
      ghost: 'text-primary-600 hover:bg-primary-50 focus:ring-primary-500',
      danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
    };

    // Size styles
    const sizeStyles = {
      sm: 'px-3 py-2 text-sm',
      md: 'px-4 py-3 text-base',
      lg: 'px-6 py-4 text-lg',
    };

    // Base styles
    const baseStyles = `
      inline-flex items-center justify-center
      whitespace-nowrap
      font-medium rounded-lg
      transition-all duration-200
      focus:outline-none focus:ring-2 focus:ring-offset-2
      ${fullWidth ? 'w-full' : ''}
      ${variantStyles[variant]}
      ${sizeStyles[size]}
      ${className}
    `.trim().replace(/\s+/g, ' ');

    // Icon spacing
    const iconSpacing = children ? (size === 'sm' ? 'gap-2' : 'gap-3') : '';
    const combinedStyles = `${baseStyles} ${iconSpacing}`;

    const iconSizeStyles = {
      sm: 'h-4 w-4',
      md: 'h-5 w-5',
      lg: 'h-6 w-6',
    };

    // Render icon
    const renderIcon = () => {
      if (loading) {
        return (
          <svg
            className="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        );
      }
      
      if (Icon) {
        return <Icon className={iconSizeStyles[size]} />;
      }
      
      return null;
    };

    // Render content
    const content = (
      <>
        {Icon && iconPosition === 'left' && renderIcon()}
        {children}
        {Icon && iconPosition === 'right' && renderIcon()}
      </>
    );

    return (
      <a
        ref={ref}
        className={combinedStyles}
        {...props}
      >
        {content}
      </a>
    );
  }
);

ButtonLink.displayName = 'ButtonLink';

